import React, { Suspense, lazy, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Nav from "./components/Nav/Nav";
import { StaffEmbeddingAlertProvider } from "./components/Nav/StaffEmbeddingAlert";
import Footer from "./components/Footer/Footer";
import DownloadQueuePanel from "./components/Downloads/DownloadQueuePanel";
import useAuth from "./hooks/useAuth";
// import LoadingSpinner from "./components/LoadingSpinner"; // Create this component

const SupportChatWidget = lazy(() => import("./components/SupportChat/SupportChatWidget"));

const Layout = () => {
  const location = useLocation();
  const { auth, setAuth, authChecked, setAuthChecked } = useAuth();
  const isProtectedRoute =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/creator/dashboard") ||
    location.pathname.startsWith("/onetimepurchase") ||
    location.pathname.startsWith("/subscriptionpayment") ||
    location.pathname.startsWith("/subscriptionupgrade") ||
    location.pathname.startsWith("/cart/checkout") ||
    location.pathname.startsWith("/admin/creator-submissions");

  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );


  axios.defaults.withCredentials = true;

  useEffect(() => {
    let cancelled = false;

    const checkauthentication = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/refresh`,
          { withCredentials: true }
        );

        const token = response.data?.accessToken;
        if (!token || cancelled) return;
        const decodedToken = jwtDecode(token);

        setAuth({
          ...decodedToken,
          RawToken: token
        });
      } catch (e) {
        if (cancelled) return;
        // Only an explicit revocation should wipe state. A generic 403
        // (bad signature, transient error) must not look like a logout.
        if (e?.response?.data?.error === "SESSION_REVOKED") {
          setAuth({});
        }
      } finally {
        if (!cancelled && typeof setAuthChecked === "function") {
          setAuthChecked(true);
        }
      }
    };

    if (!auth?.role) {
      checkauthentication();
    } else if (typeof setAuthChecked === "function") {
      setAuthChecked(true);
    }

    return () => {
      cancelled = true;
    };
  }, [auth?.role, setAuth, setAuthChecked]);

  useEffect(() => {
    const controller = new AbortController();

    const logFrontendPage = async () => {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/log/request-logs/frontend-page`,
          {
            path: location.pathname || "/",
            url: `${location.pathname || "/"}${location.search || ""}`,
            query: Object.fromEntries(new URLSearchParams(location.search || "")),
            referrer: document.referrer || "",
            title: document.title || "",
          },
          {
            headers: auth?.RawToken
              ? { Authorization: `Bearer ${auth.RawToken}` }
              : {},
            signal: controller.signal,
          },
        );
      } catch (error) {
        if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
          console.error("Frontend page logging failed:", error);
        }
      }
    };

    logFrontendPage();

    return () => {
      controller.abort();
    };
  }, [location.pathname, location.search, auth?.RawToken]);

  if (!authChecked && isProtectedRoute) {
    return <LoadingSpinner />;
  }

  return (
    <div className="App" id="app-root">
      <StaffEmbeddingAlertProvider auth={auth}>
        {!location.pathname.startsWith("/admin") && <Nav />}
        {!location.pathname.startsWith("/admin") && <DownloadQueuePanel />}
        <Outlet />
      </StaffEmbeddingAlertProvider>
      {!location.pathname.startsWith("/admin") &&
        !location.pathname.startsWith("/dashboard") &&
        !location.pathname.startsWith("/creator/dashboard") && <Footer />}
      {!location.pathname.startsWith("/admin") &&
        !location.pathname.startsWith("/dashboard") &&
        !location.pathname.startsWith("/creator/dashboard") && (
          <Suspense fallback={null}>
            <SupportChatWidget
              hideLauncher={
                location.pathname === "/" ||
                location.pathname.startsWith("/collection/")
              }
            />
          </Suspense>
        )}
    </div>
  );
};

export default Layout;




